
import * as SwitchPrimitives from '@radix-ui/react-switch';
import { Check, Loader, X } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/lib/utils';

interface SwitchProps
  extends React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root> {
  pending?: boolean;
  size?: 'default' | 'lg';
  showText?: boolean;
}

const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitives.Root>,
  SwitchProps
>(
  (
    { className, pending, showText = false, size = 'default', ...props },
    ref,
  ) => {
    const iconClass = {
      'h-3 w-3': size === 'default',
      'h-5 w-5': size === 'lg',
    };

    return (
      <SwitchPrimitives.Root
        className={cn(
          'relative peer inline-flex shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=unchecked]:bg-popover',
          {
            'h-6 w-13': size === 'default' && showText,
            'h-6 w-11': size === 'default' && !showText,
            'h-10 w-20': size === 'lg',
          },
          className,
        )}
        {...props}
        ref={ref}
      >
        {props.checked && showText && (
          <span className="absolute text-background font-bold text-xs left-1">
            On
          </span>
        )}
        <SwitchPrimitives.Thumb
          className={cn(
            'pointer-events-none flex items-center justify-center rounded-full bg-background shadow-lg ring-0 transition-transform data-[state=unchecked]:translate-x-0',
            {
              'h-5 w-5 data-[state=checked]:translate-x-7':
                size === 'default' && showText,
              'h-5 w-5 data-[state=checked]:translate-x-5':
                size === 'default' && !showText,
              'h-9 w-9 data-[state=checked]:translate-x-10': size === 'lg',
            },
          )}
        >
          {pending ? (
            <Loader className={cn('animate-spin text-primary', iconClass)} />
          ) : props.checked ? (
            <Check className={cn('text-primary', iconClass)} />
          ) : (
            <X className={cn('text-secondary', iconClass)} />
          )}
        </SwitchPrimitives.Thumb>
        {!props.checked && showText && (
          <span className="absolute right-0 text-background text-foreground text-xs font-bold right-1">
            Off
          </span>
        )}
      </SwitchPrimitives.Root>
    );
  },
);
Switch.displayName = SwitchPrimitives.Root.displayName;

export { Switch };
